import Axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import router from '@/router';
import SecureLS from 'secure-ls';
import store from '@/store';
import { apiUrlPrefix } from '@/utils/helpers';
interface ISessionData {
  auth?: {
    user?: {
      token?: string;
    };
  };
}
const ls = new SecureLS({
  isCompression: false,
});

async function handleUnauthorized() {
  // never let session-teardown failures replace the original API error —
  // downstream catch blocks need e.response to build user-facing messages
  try {
    localStorage.clear();
    await store.dispatch('auth/logOut', null);
    await store.dispatch('closeAllDialogs', null, { root: true });
    if (router.currentRoute.name !== 'accounts.signin') {
      await router.replace({ name: 'accounts.signin' }).catch((err) => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('Router push error:', err);
        }
      });
    }
  } catch (err) {
    console.error('Failed to tear down session after 401:', err);
  }
}
const Api = (): AxiosInstance => {
  const baseDomain =
    process.env.VUE_APP_BASE_URL || 'https://api.default.kairosafrika.cloud'; // Fallback if env var is not set
  const resolvedBaseURL = `${baseDomain}${apiUrlPrefix}`; // Assuming all sms-service APIs are under /v1 path
  const baseHeader: AxiosInstance = Axios.create({
    baseURL: resolvedBaseURL,
    timeout: 120000,
  });

  baseHeader.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (!config.headers.getContentType() && !config.headers.getAccept()) {
        config.headers.setAccept('application/json');
        config.headers.setContentType('application/json');
      }

      // Always try to add Bearer token for calls to sms-service
      const sessionData = ls.get('cloud.access');
      if (sessionData && typeof sessionData === 'string') {
        try {
          const parsedSession = JSON.parse(sessionData) as ISessionData;
          const token = parsedSession?.auth?.user?.token;
          if (token) {
            config.headers.setAuthorization(`Bearer ${token}`);
          }
        } catch (e) {
          console.error('Failed to parse session data from SecureLS:', e);
        }
      }
      return config;
    },
    (error) => {
      console.error('API Request Error Interceptor (sms-service):', error);
      return Promise.reject(error);
    }
  );

  baseHeader.interceptors.response.use(
    (response: AxiosResponse) => {
      return Promise.resolve(response);
    },
    async (error) => {
      if (error.response) {
        if (error.response.status === 401) {
          await handleUnauthorized();
        }
      }
      return Promise.reject(error);
    }
  );
  return baseHeader;
};

export { Api };
